import { useState } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Link,
  Button,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
  Chip,
  IconButton,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { Play } from 'lucide-react';
import { useNavigate } from 'react-router';
import type { CompanyAccount } from '@/lib/types/mise/company';
import {
  useUnlinkedAccounts,
  useLinkAccountToCompany,
  useExecuteBatch,
} from '@/lib/query/hooks/useCompanies';

interface CompanyAccountListProps {
  accounts: CompanyAccount[];
  isLoading?: boolean;
  error?: Error | null;
  companyId: string;
}

export function CompanyAccountList({
  accounts,
  isLoading,
  error,
  companyId,
}: CompanyAccountListProps) {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // 未紐づけアカウント一覧を取得
  const { data: unlinkedAccounts = [], isLoading: unlinkedAccountsLoading } = useUnlinkedAccounts();

  // アカウント追加のMutation
  const linkAccountMutation = useLinkAccountToCompany();

  // バッチ実行関連のstate
  const [executeBatchDialogOpen, setExecuteBatchDialogOpen] = useState(false);
  const [executeBatchAccountId, setExecuteBatchAccountId] = useState<string>('');
  const [executeBatchForceAction, setExecuteBatchForceAction] = useState(false);
  const [executeBatchInvokeBatchCache, setExecuteBatchInvokeBatchCache] = useState(true);
  const [executeBatchInvokeBatchLlm, setExecuteBatchInvokeBatchLlm] = useState(true);
  const [executeBatchInvokeBatchStoreUsage, setExecuteBatchInvokeBatchStoreUsage] = useState(true);
  const [executeBatchForceDailySalesInitialize, setExecuteBatchForceDailySalesInitialize] =
    useState(false);
  const executeBatch = useExecuteBatch();

  const handleAccountClick = (accountId: string) => {
    navigate(`/mise/accounts/${accountId}`);
  };
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '未設定';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('ja-JP');
    } catch {
      return dateString;
    }
  };

  const handleOpenDialog = () => {
    setDialogOpen(true);
    setSelectedAccountId('');
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAccountId('');
  };

  const handleAddClick = () => {
    if (!selectedAccountId) {
      setSnackbarMessage('アカウントを選択してください');
      setSnackbarOpen(true);
      return;
    }
    setConfirmDialogOpen(true);
  };

  const handleConfirmAdd = async () => {
    try {
      await linkAccountMutation.mutateAsync({
        companyId,
        accountId: selectedAccountId,
      });
      setConfirmDialogOpen(false);
      setDialogOpen(false);
      setSelectedAccountId('');
      setSnackbarMessage('アカウントを追加しました');
      setSnackbarOpen(true);
    } catch (error) {
      setConfirmDialogOpen(false);
      setSnackbarMessage(error instanceof Error ? error.message : 'アカウントの追加に失敗しました');
      setSnackbarOpen(true);
    }
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleOpenExecuteBatchDialog = (accountId: string) => {
    setExecuteBatchAccountId(accountId);
    setExecuteBatchForceAction(false);
    setExecuteBatchInvokeBatchCache(true);
    setExecuteBatchInvokeBatchLlm(true);
    setExecuteBatchInvokeBatchStoreUsage(true);
    setExecuteBatchForceDailySalesInitialize(false);
    setExecuteBatchDialogOpen(true);
  };

  const handleCloseExecuteBatchDialog = () => {
    setExecuteBatchDialogOpen(false);
    setExecuteBatchAccountId('');
    setExecuteBatchForceAction(false);
    setExecuteBatchInvokeBatchCache(true);
    setExecuteBatchInvokeBatchLlm(true);
    setExecuteBatchInvokeBatchStoreUsage(true);
    setExecuteBatchForceDailySalesInitialize(false);
  };

  const handleExecuteBatch = async () => {
    if (
      !executeBatchInvokeBatchCache &&
      !executeBatchInvokeBatchLlm &&
      !executeBatchInvokeBatchStoreUsage
    ) {
      setSnackbarMessage('実行対象のバッチが選択されていません');
      setSnackbarOpen(true);
      return;
    }
    try {
      await executeBatch.mutateAsync({
        accountId: executeBatchAccountId,
        forceAction: executeBatchForceAction,
        invokeBatchCache: executeBatchInvokeBatchCache,
        invokeBatchLlm: executeBatchInvokeBatchLlm,
        invokeBatchStoreUsage: executeBatchInvokeBatchStoreUsage,
        forceDailySalesInitialize: executeBatchForceDailySalesInitialize,
      });
      handleCloseExecuteBatchDialog();
      setSnackbarMessage('バッチ実行を開始しました');
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage(error instanceof Error ? error.message : 'バッチ実行に失敗しました');
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>読み込み中...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          {error instanceof Error ? error.message : 'アカウント一覧の取得に失敗しました。'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* アカウント追加セクション */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleOpenDialog}
          disabled={unlinkedAccountsLoading || unlinkedAccounts.length === 0}
        >
          アカウント追加
        </Button>
      </Box>

      {/* アカウント選択ダイアログ */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>アカウントを選択</DialogTitle>
        <DialogContent>
          {unlinkedAccountsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : unlinkedAccounts.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 2 }}>
              追加可能なアカウントがありません
            </Typography>
          ) : (
            <RadioGroup
              value={selectedAccountId}
              onChange={(e) => setSelectedAccountId(e.target.value)}
            >
              <List>
                {unlinkedAccounts.map((account) => (
                  <ListItem key={account.id} disablePadding>
                    <ListItemButton onClick={() => setSelectedAccountId(account.id)} dense>
                      <FormControlLabel
                        value={account.id}
                        control={<Radio />}
                        label={
                          <Box>
                            <Typography variant="body1">
                              {account.code || account.id} - {account.name || account.id}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ID: {account.id}
                            </Typography>
                          </Box>
                        }
                        sx={{ margin: 0, width: '100%' }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </RadioGroup>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>キャンセル</Button>
          <Button onClick={handleAddClick} variant="contained" disabled={!selectedAccountId}>
            追加
          </Button>
        </DialogActions>
      </Dialog>

      {/* 確認ダイアログ */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        sx={{
          '& .MuiDialog-container': {
            zIndex: 1400,
          },
        }}
      >
        <DialogTitle>アカウント追加の確認</DialogTitle>
        <DialogContent>
          <Typography>以下のアカウントをこの企業に追加しますか？</Typography>
          {selectedAccountId && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              {(() => {
                const account = unlinkedAccounts.find((a) => a.id === selectedAccountId);
                return account ? (
                  <>
                    <Typography variant="body1" fontWeight="medium">
                      {account.code || account.id} - {account.name || account.id}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {account.id}
                    </Typography>
                  </>
                ) : null;
              })()}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} disabled={linkAccountMutation.isPending}>
            キャンセル
          </Button>
          <Button
            onClick={handleConfirmAdd}
            variant="contained"
            disabled={linkAccountMutation.isPending}
          >
            {linkAccountMutation.isPending ? '追加中...' : '追加'}
          </Button>
        </DialogActions>
      </Dialog>

      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer
          sx={{
            overflowX: 'auto',
            maxWidth: '100%',
            '& .MuiTable-root': {
              minWidth: 650,
            },
          }}
        >
          <Table sx={{ minWidth: 650, whiteSpace: 'nowrap' }} aria-label="アカウントテーブル">
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>アカウントID</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>契約</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>コード</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>アカウント名</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>基準日</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>last_sync_data_version</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>アクション</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography color="text.secondary">アカウントが見つかりませんでした</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                accounts.map((account, index) => (
                  <TableRow
                    key={`${account.id}-${index}`}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'action.hover',
                        cursor: 'pointer',
                      },
                    }}
                    onClick={() => handleAccountClick(account.id)}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Link
                        component="button"
                        variant="body1"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAccountClick(account.id);
                        }}
                        sx={{
                          color: 'primary.main',
                          textDecoration: 'none',
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {account.id}
                      </Link>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Chip
                        label={account.has_contract ? '契約あり' : '契約なし'}
                        size="small"
                        color={account.has_contract ? 'success' : 'default'}
                        variant={account.has_contract ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{account.code}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{account.name}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {formatDate(account.base_date)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {account.last_sync_data_version ?? '-'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenExecuteBatchDialog(account.id)}
                        title="バッチ実行"
                      >
                        <Play size={18} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* バッチ実行確認ダイアログ */}
      <Dialog
        open={executeBatchDialogOpen}
        onClose={handleCloseExecuteBatchDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>バッチ実行の確認</DialogTitle>
        <DialogContent>
          <Typography>以下のアカウントでバッチを実行しますか？</Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body1" fontWeight="medium">
              アカウントID: {executeBatchAccountId}
            </Typography>
          </Box>
          <FormControlLabel
            control={
              <Checkbox
                checked={executeBatchInvokeBatchCache}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setExecuteBatchInvokeBatchCache(checked);
                  if (!checked) {
                    setExecuteBatchForceDailySalesInitialize(false);
                  }
                }}
              />
            }
            label="キャッシュバッチ実行（invoke_batch_cache）"
            sx={{ mt: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={executeBatchForceDailySalesInitialize}
                onChange={(e) => setExecuteBatchForceDailySalesInitialize(e.target.checked)}
                disabled={!executeBatchInvokeBatchCache}
              />
            }
            label="初期化実行（force_daily_sales_initialize）"
            sx={{ mt: 0, ml: 3 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={executeBatchInvokeBatchLlm}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setExecuteBatchInvokeBatchLlm(checked);
                  if (!checked) {
                    setExecuteBatchForceAction(false);
                  }
                }}
              />
            }
            label="LLMバッチ実行（invoke_batch_llm）"
            sx={{ mt: 1 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={executeBatchForceAction}
                onChange={(e) => setExecuteBatchForceAction(e.target.checked)}
                disabled={!executeBatchInvokeBatchLlm}
              />
            }
            label="強制実行（force_action）"
            sx={{ mt: 0, ml: 3 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={executeBatchInvokeBatchStoreUsage}
                onChange={(e) => setExecuteBatchInvokeBatchStoreUsage(e.target.checked)}
              />
            }
            label="利用状況バッチ実行（invoke_batch_store_usage）"
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseExecuteBatchDialog} disabled={executeBatch.isPending}>
            キャンセル
          </Button>
          <Button
            onClick={handleExecuteBatch}
            variant="contained"
            disabled={executeBatch.isPending}
          >
            {executeBatch.isPending ? '実行中...' : '実行'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* スナックバー */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={snackbarMessage}
      />
    </Box>
  );
}
